<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
  provide,
  shallowRef,
  watch,
} from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from './erros';
import { useI18n } from 'vue-i18n';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useAcesso } from 'dashboard/composables/useAcesso';

import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

import AgentSearch from './AgentSearch.vue';
import IdentitySection from './sections/IdentitySection.vue';
import PromptSection from './sections/PromptSection.vue';
import KnowledgeSection from './sections/KnowledgeSection.vue';
import GuardrailsSection from './sections/GuardrailsSection.vue';
import SettingsSection from './sections/SettingsSection.vue';
import FollowupSection from './sections/FollowupSection.vue';
import AudioSection from './sections/AudioSection.vue';
import TriagemSection from './sections/TriagemSection.vue';
import ToolsSection from './sections/ToolsSection.vue';
import ChannelSection from './sections/ChannelSection.vue';
import Playground from './sections/Playground.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = () => route.params.accountId;
const agentId = () => route.params.agentId;

// PA-01 (T11.5) — de doze seções para oito, aprovado por Murilo em 2026-09-02.
//
// SAÍRAM, e o porquê de cada uma:
//   guardrails  VOLTOU em 2026-09-10 (AGENTS-GUARDRAILS-01). O motivo de ter saído estava
//               certo — "um interruptor que não liga nada" — e era literal: o runtime nunca
//               leu `payload["guardrails"]`. Agora lê, e os três guards que já rodavam
//               fixos (h9_artifacts, action_claim, empty_output) passaram a ser
//               configuráveis por agente. PII e jailbreak seguem na onda 2.
//   media       cinco dos seis interruptores são da onda 2. O sexto, debounce, é da onda 1
//               e virou campo em Identidade, que é onde ele pertence: é comportamento do
//               agente, não de mídia.
//   knowledge   aponta para uma base no ChatData, que a D14 deixou fora do runtime. Some
//               de vez: quando o RAG chegar na onda 2 ele será nosso, e a tela que volta
//               não é esta.
//   handoff     VOLTOU em 2026-09-11 (AGENTS-HANDOFF-01). Tinha saído por ser um campo só
//               (o time de fallback, que foi parar em Settings); agora é o bloco `handoff`
//               inteiro — quando o agente se cala, quando volta, e a tool de pedir humano.
//               E SAIU de novo em 22/09/2026, agora para dentro de Ferramentas: o switch da
//               tool morava lá e o que ela liga morava aqui. A configuração inteira passou a
//               ser o diálogo do card "Pedir atendimento humano" (decisão do Murilo).
//
// Adiar não é apagar: `media` volta quando a onda 2 entregar o que ela configura. O
// componente segue no repositório, sem uso, de propósito.
const SECTIONS = [
  // O prompt é a primeira seção e é de todo mundo: é o que o cliente veio editar. Até
  // 23/09/2026 ele morava dentro de "Modelo", e o cliente via uma seção chamada Modelo com
  // só o prompt dentro -- o bloco de modelo já era escondido dele.
  { id: 'prompt', icon: 'i-lucide-scroll-text', component: PromptSection },
  // Qual LLM responde é dado de plataforma: a seção inteira some para quem não é super admin
  // (decisão do Murilo, 23/09/2026).
  {
    id: 'identity',
    icon: 'i-lucide-cpu',
    component: IdentitySection,
    plataforma: true,
  },
  // Sem `plataforma` desde 22/09/2026: o cliente e a faixa de leitura veem a lista, sem mexer
  // nela, e o cliente configura o atendimento humano pelo card "Pedir atendimento humano".
  // O que é de plataforma dentro dela a própria seção esconde.
  { id: 'tools', icon: 'i-lucide-wrench', component: ToolsSection },
  { id: 'knowledge', icon: 'i-lucide-library', component: KnowledgeSection },
  { id: 'followup', icon: 'i-lucide-alarm-clock', component: FollowupSection },
  {
    id: 'audio',
    icon: 'i-lucide-mic',
    component: AudioSection,
    plataforma: true,
  },
  {
    id: 'triagem',
    icon: 'i-lucide-moon',
    component: TriagemSection,
    plataforma: true,
  },
  // O administrador liga e desliga caixa no agente (VISAO-01, D1, 29/09/2026); a faixa de leitura
  // não. O que é de plataforma dentro dela (link de teste, caixa de site, números da Powerbizz) a
  // própria seção esconde.
  {
    id: 'channel',
    icon: 'i-lucide-plug',
    component: ChannelSection,
    doAdministrador: true,
  },
  {
    id: 'guardrails',
    icon: 'i-lucide-shield-check',
    component: GuardrailsSection,
  },
  {
    id: 'settings',
    icon: 'i-lucide-settings',
    component: SettingsSection,
    plataforma: true,
  },
  { id: 'playground', icon: 'i-lucide-flask-conical', component: Playground },
];

// O menu lateral nasce COLAPSADO, em ícones. A tela do agente é de trabalho — o operador
// passa o tempo dentro de uma seção, não navegando entre elas — e 56px de rail permanente
// tiram largura de onde ela faz falta (o editor de prompt, a lista de tools, o rastro).
//
// Expandir no hover é OVERLAY, não empurra: se o conteúdo se mexesse a cada passagem de
// mouse, o menu viraria estorvo. Quem quiser o menu aberto de verdade FIXA — e a escolha
// dura entre sessões, porque ela é do jeito de trabalhar de cada um e não desta visita.
const CHAVE_FIXADO = 'pa-menu-fixado';
const fixado = ref(false);
const sobrevoando = ref(false);
const expandido = computed(() => fixado.value || sobrevoando.value);

function alternarFixado() {
  fixado.value = !fixado.value;
  try {
    localStorage.setItem(CHAVE_FIXADO, fixado.value ? '1' : '0');
  } catch (e) {
    // Aba anônima ou storage bloqueado: o menu continua funcionando, só não lembra.
  }
}

// As seções de PLATAFORMA somem para quem não é super-admin. O cliente (22/09/2026) vê o
// prompt, o conhecimento, os guardrails, o follow-up e Ferramentas — esta só para leitura,
// menos a configuração de "Pedir atendimento humano", que era a aba Atendimento humano. As
// caixas (29/09/2026) são do administrador, e não da faixa de leitura.
//
// Esconder a tela NÃO é a trava: o backend recusa a chave de config de uma seção escondida
// (`CONFIG_KEYS_DO_CLIENTE` é lista de permissão). Isto aqui evita que a tela ofereça o que
// o servidor vai negar, que é a forma mais cara de descobrir uma permissão.
const { isSuperAdmin, isAdmin } = useAdmin();
const { decide } = useAcesso();

// Quem NÃO é super-admin e NÃO é administrador da conta chega aqui só por ser `agent` com a
// flag `power_agents` ligada — o backend não deixa mais ninguém entrar. Essa é a faixa de
// leitura, e o `SectionShell` a consome para desabilitar os controles e sumir com o Save.
//
// ACESSO-01 (L8b): com a chave ACESSO_GRANULAR ligada, quem edita o agente é quem tem o
// `poweragents:write` (`power_agents#update`, `#connect_inbox`), e a faixa de leitura é quem só vê;
// desligada, o administrador, como acima. As seções de plataforma seguem no `isSuperAdmin`.
const editaAgente = computed(() =>
  decide('poweragents:write', () => isSuperAdmin.value || isAdmin.value)
);
const somenteLeitura = computed(() => !editaAgente.value);
provide('powerAgentSomenteLeitura', somenteLeitura);
const secoesVisiveis = computed(() =>
  SECTIONS.filter(s => {
    if (isSuperAdmin.value) return true;
    if (s.plataforma) return false;
    return !s.doAdministrador || editaAgente.value;
  })
);

const SECTION_LABELS = {
  prompt: 'PROMPT',
  identity: 'IDENTITY',
  knowledge: 'KNOWLEDGE',
  settings: 'SETTINGS',
  followup: 'FOLLOWUP',
  // Faltava, e era a única das dez: `sectionLabel('audio')` montava a chave
  // `POWER_AGENTS.SECTIONS.undefined`, que não existe, e o rail renderizava a string crua.
  audio: 'AUDIO',
  triagem: 'TRIAGEM',
  tools: 'TOOLS',
  channel: 'CHANNEL',
  guardrails: 'GUARDRAILS',
  playground: 'PLAYGROUND',
};

const agent = ref(null);
const isFetching = ref(false);
// A primeira seção é visível para todos os papéis: abrir numa seção de plataforma deixaria o
// cliente diante de um painel vazio.
// A seção aberta vai na URL (`?secao=`): um F5 ou um link compartilhado voltava para o Prompt.
const secaoDaUrl = SECTIONS.some(s => s.id === route.query.secao)
  ? route.query.secao
  : null;
const activeSection = ref(secaoDaUrl || SECTIONS[0].id);
const isPublishing = ref(false);
const publishDialogRef = ref(null);

// Local counter of unpublished structural changes made this session, on top of
// whatever the server already reports as pending. Structural edits (add/remove
// tool, guardrail change, model router, channel) call `markPending`.
const localPending = ref(0);

const pendingCount = computed(() => {
  const serverPending = agent.value?.pending_changes ? 1 : 0;
  return Math.max(localPending.value, serverPending);
});

const activeComponent = computed(
  () => secoesVisiveis.value.find(s => s.id === activeSection.value)?.component
);

watch(activeSection, secao => {
  if (route.query.secao === secao) return;
  router.replace({ query: { ...route.query, secao } });
});

// RASCUNHO DA SEÇÃO ABERTA. Só uma seção está montada por vez, então basta um registro: ela o
// entrega ao montar (`useRascunhoDaSecao`) e o tira ao desmontar.
const secaoAberta = shallowRef(null);
const secaoSuja = computed(() => Boolean(secaoAberta.value?.suja()));
function registrarSecao(registro) {
  secaoAberta.value = registro;
}
function soltarSecao(registro) {
  if (secaoAberta.value === registro) secaoAberta.value = null;
}
async function salvarSecaoAberta() {
  return secaoAberta.value?.salvar ? secaoAberta.value.salvar() : true;
}

// GUARDA. Toda saída da seção suja (outra seção, Playground, busca, voltar, sair da rota) passa
// por aqui: com edição pendente, pergunta antes; sem, segue direto.
const guardaRef = ref(null);
const salvandoNaGuarda = ref(false);
let acaoPendente = null;
function pedirTroca(acao) {
  if (!secaoSuja.value) {
    acao();
    return;
  }
  acaoPendente = acao;
  guardaRef.value?.open();
}
function seguirTroca() {
  const acao = acaoPendente;
  acaoPendente = null;
  guardaRef.value?.close();
  acao?.();
}
async function salvarETrocar() {
  salvandoNaGuarda.value = true;
  try {
    // Se o salvar falhar, a pessoa fica onde está: o toast da seção já disse o motivo.
    if (await salvarSecaoAberta()) seguirTroca();
  } finally {
    salvandoNaGuarda.value = false;
  }
}
function descartarETrocar() {
  // A seção desmonta na troca e remonta do agente gravado: descartar é só não salvar.
  secaoAberta.value = null;
  seguirTroca();
}
// Fechar a guarda por Esc ou pelo X é o mesmo que ficar: a ação pedida não acontece.
function esquecerTroca() {
  acaoPendente = null;
}
function ficar() {
  esquecerTroca();
  guardaRef.value?.close();
}
const abrirSecao = id =>
  pedirTroca(() => {
    activeSection.value = id;
  });

let saindoComPermissao = false;
onBeforeRouteLeave(to => {
  if (saindoComPermissao || !secaoSuja.value) return true;
  pedirTroca(() => {
    saindoComPermissao = true;
    router.push(to.fullPath);
  });
  return false;
});
// Fechar a aba ou recarregar: o navegador mostra o aviso dele (o texto não é nosso).
function avisarAoSairDaAba(evento) {
  if (!secaoSuja.value) return;
  evento.preventDefault();
  evento.returnValue = '';
}

function sectionLabel(id) {
  return t(`POWER_AGENTS.SECTIONS.${SECTION_LABELS[id]}`);
}

async function fetchAgent() {
  isFetching.value = true;
  try {
    const data = await powerAgentsApi.show(accountId(), agentId());
    if (data.error || !data.id) throw new Error('not found');
    agent.value = data;
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.FETCH_ERROR')));
    router.push({
      name: 'power_agents_index',
      params: { accountId: accountId() },
    });
  } finally {
    isFetching.value = false;
  }
}

// Sections emit `saved` with the fresh agent object (from a runtime PATCH) so
// the parent keeps the canonical copy in sync.
function onSectionSaved(updated) {
  if (updated && updated.id) agent.value = updated;
}

// Structural changes bump the pill optimistically, then reconcile against the server so a
// reverted edit clears it (the local counter alone only ever grew — the pill got stuck).
async function markPending() {
  localPending.value += 1;
  try {
    const data = await powerAgentsApi.show(accountId(), agentId());
    if (data && data.id) {
      agent.value = data;
      if (!data.pending_changes) localPending.value = 0;
    }
  } catch (e) {
    // keep the optimistic count on a transient failure
  }
}

// Levar até a seção certa é metade; a outra metade é DIZER qual campo. Abrir Settings e
// deixar a pessoa varrer sete campos atrás da manutenção é o defeito que a busca existe para
// consertar — por isso o resultado carrega uma âncora e ela é grifada por três segundos.
const buscaRef = ref(null);
const DESTAQUE_MS = 3000;
let limpaDestaque = null;

async function irDireto({ section, anchor: alvo }) {
  activeSection.value = section;
  // Dois tiques: o primeiro monta a seção, o segundo deixa o DOM dela pronto para consulta.
  await nextTick();
  await nextTick();
  const el = document.querySelector(`[data-busca="${alvo}"]`);
  if (!el) return;

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  el.classList.add('pa-grifado');
  clearTimeout(limpaDestaque);
  limpaDestaque = setTimeout(
    () => el.classList.remove('pa-grifado'),
    DESTAQUE_MS
  );
}

// A busca e o aviso do Playground também passam pela guarda: levam a outra seção.
function irPara(destino) {
  pedirTroca(() => irDireto(destino));
}

// ⌘K busca; ⌘S salva a seção aberta (antes o ⌘S abria o "salvar página" do navegador).
function atalhoDeBusca(evento) {
  if (!(evento.metaKey || evento.ctrlKey)) return;
  const tecla = evento.key.toLowerCase();
  if (tecla === 'k') {
    evento.preventDefault();
    buscaRef.value?.focar();
  } else if (tecla === 's' && secaoAberta.value?.salvar) {
    evento.preventDefault();
    if (!somenteLeitura.value) salvarSecaoAberta();
  }
}

function goBack() {
  pedirTroca(() => {
    saindoComPermissao = true;
    router.push({
      name: 'power_agents_index',
      params: { accountId: accountId() },
    });
  });
}

const deleteDialogRef = ref(null);
const isDeleting = ref(false);

function openDelete() {
  deleteDialogRef.value.open();
}

async function confirmDelete() {
  isDeleting.value = true;
  try {
    await powerAgentsApi.destroy(accountId(), agentId());
    useAlert(t('POWER_AGENTS.DELETE.SUCCESS'));
    router.push({ name: 'power_agents_index' });
  } catch (e) {
    // O backend explica POR QUE não deu (ex.: a infraestrutura remota do agente não pôde ser
    // removida, e apagar só o registro local deixaria um agente órfão respondendo). Essa
    // mensagem chegava e era descartada — o usuário via "não foi possível" e não sabia o que
    // fazer. Mesmo defeito que o erro de criar tinha.
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.DELETE.ERROR')));
  } finally {
    isDeleting.value = false;
    deleteDialogRef.value.close();
  }
}

function openPublish() {
  publishDialogRef.value.open();
}

async function confirmPublish() {
  isPublishing.value = true;
  try {
    // "Salvar e publicar": o publish leva o que está GRAVADO, então a seção suja vai antes.
    if (secaoSuja.value && !(await salvarSecaoAberta())) return;
    const resp = await powerAgentsApi.publish(accountId(), agentId());
    if (resp.error) throw new Error(resp.error);
    useAlert(t('POWER_AGENTS.PUBLISH.SUCCESS'));
    localPending.value = 0;
    publishDialogRef.value.close();
    fetchAgent();
  } catch (e) {
    // O publish é onde o código de erro sozinho menos ajuda: "(runtime_unreachable)" não
    // diz o que fazer. O extrator devolve a frase traduzida quando o código é conhecido e o
    // `detail` do backend quando não é.
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.PUBLISH.FAILED')));
  } finally {
    isPublishing.value = false;
  }
}

// Shared context for section components.
provide('powerAgentContext', {
  accountId,
  agentId,
  // O nome legível do agente. O editor de prompt mostrava o `agentId` cru no canto — quem abre
  // o prompt da Eva via "77". Cai no slug, e depois no id, quando o nome não veio.
  agentName: () => agent.value?.bot_name || agent.value?.slug || null,
  markPending,
  onSectionSaved,
  // Apagar é ação de agente, não de lista: mora na zona de risco da Identidade. O diálogo
  // continua aqui porque é daqui que se navega de volta depois de apagar.
  openDelete,
  // O playground precisa saber que existe alteração não publicada — é o que explica uma
  // tool recém-criada "não existir" no teste — e precisa poder publicar dali mesmo.
  pendingCount,
  openPublish,
  // Levar a pessoa a um campo de outra seção, como a busca leva (L-31: o aviso do Playground de que
  // o agente não analisa arquivos leva a "Voz e anexos"). Só para a seção que ela vê: as de
  // plataforma somem para quem não é super admin.
  irPara,
  temSecao: id => secoesVisiveis.value.some(secao => secao.id === id),
  // A seção aberta entrega o rascunho dela (`useRascunhoDaSecao`) e o tira ao desmontar.
  registrarSecao,
  soltarSecao,
  // O modo do Playground (L-27) se troca no bloco do link de teste, em Canais, e o Playground o lê
  // desta cópia do agente: a troca chega a ela sem buscar o agente de novo.
  modoDoPlaygroundMudou: modo => {
    if (agent.value) agent.value = { ...agent.value, playground_mode: modo };
  },
});

onMounted(() => {
  try {
    fixado.value = localStorage.getItem(CHAVE_FIXADO) === '1';
  } catch (e) {
    fixado.value = false;
  }
});

onMounted(fetchAgent);

onMounted(() => {
  window.addEventListener('keydown', atalhoDeBusca);
  window.addEventListener('beforeunload', avisarAoSairDaAba);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', atalhoDeBusca);
  window.removeEventListener('beforeunload', avisarAoSairDaAba);
  clearTimeout(limpaDestaque);
});
</script>

<template>
  <!-- overflow-hidden aqui é o que faz o menu ficar parado. Sem ele, a página inteira rola
       (o h-full não tem altura para resolver contra) e o `overflow-y-auto` da <section>
       nunca chega a valer: a barra que aparece é a de fora, arrastando o menu junto.
       Em tela cheia o h-full resolve contra o #app (h-screen), não mais contra o
       SettingsWrapper — daí o min-h-0, como em PowerAppStandalone. -->
  <div class="flex flex-col w-full h-full min-h-0 overflow-hidden">
    <!-- Top bar: back + title + publish pill + test -->
    <!-- No celular a barra fica com voltar, nome e o Publicar: a busca some abaixo de `sm`,
         como no cabeçalho das outras configurações, e o Playground já está no menu lateral. -->
    <div
      class="flex items-center justify-between gap-3 px-3 py-3 border-b sm:px-6 sm:py-4 border-n-weak"
    >
      <div class="flex items-center gap-3 min-w-0">
        <!-- No celular, só a seta: o texto segue para o leitor de tela (sr-only) e o nome do
             agente fica com o espaço. `sm:truncate` depois do `not-sr-only`, que zera o nowrap. -->
        <Button icon="i-lucide-arrow-left" slate faded sm @click="goBack">
          <span class="min-w-0 sr-only sm:not-sr-only sm:truncate">
            {{ t('POWER_AGENTS.BACK') }}
          </span>
        </Button>
        <div v-if="agent" class="min-w-0">
          <h1 class="text-base font-semibold truncate text-n-slate-12">
            {{ agent.bot_name || agent.slug }}
          </h1>
        </div>
      </div>

      <div v-if="agent" class="flex items-center gap-2 shrink-0">
        <AgentSearch
          ref="buscaRef"
          class="hidden sm:block"
          :secoes="secoesVisiveis.map(s => s.id)"
          @ir="irPara"
        />
        <!-- Âmbar é "falta publicar" (DESIGN.md: aviso), em faded: texto em tinta sobre âmbar
             claro. O sólido era branco sobre âmbar, 1,6:1. Sem pendência, vira o estado. -->
        <Button
          v-if="pendingCount > 0"
          icon="i-lucide-rocket"
          :label="t('POWER_AGENTS.PUBLISH.PILL')"
          amber
          faded
          sm
          data-publicar
          @click="openPublish"
        />
        <Button
          v-else
          icon="i-lucide-check"
          :label="t('POWER_AGENTS.PUBLISH.PILL_NONE')"
          slate
          ghost
          sm
          disabled
          data-publicar
        />
        <Button
          icon="i-lucide-flask-conical"
          :label="t('POWER_AGENTS.PUBLISH.TESTID')"
          slate
          sm
          class="hidden sm:inline-flex"
          @click="abrirSecao('playground')"
        />
      </div>
    </div>

    <div v-if="isFetching" class="flex items-center justify-center flex-1">
      <Spinner />
    </div>

    <!-- Body: left rail + panel -->
    <div v-else-if="agent" class="flex flex-1 min-h-0">
      <!-- O rail reserva SEMPRE a largura colapsada; o painel expandido flutua por cima.
           É o que faz o hover não empurrar o conteúdo. Quando fixado, a largura reservada
           passa a ser a inteira e não há mais sobreposição. -->
      <div
        class="relative shrink-0 transition-[width] duration-150"
        :class="fixado ? 'w-56' : 'w-14'"
        @mouseenter="sobrevoando = true"
        @mouseleave="sobrevoando = false"
      >
        <nav
          class="absolute inset-y-0 left-0 flex flex-col gap-0.5 p-2 border-r border-n-weak bg-n-background overflow-y-auto overflow-x-hidden transition-[width] duration-150"
          :class="expandido ? 'w-56 shadow-lg' : 'w-14'"
        >
          <!-- aria-label: recolhido, o botão é só ícone e o tooltip não dá nome a ele. -->
          <button
            v-for="section in secoesVisiveis"
            :key="section.id"
            v-tooltip.right="expandido ? '' : sectionLabel(section.id)"
            :aria-label="sectionLabel(section.id)"
            :aria-current="activeSection === section.id ? 'page' : undefined"
            class="relative flex items-center gap-2.5 px-3 py-2 text-sm rounded-lg text-left transition-colors"
            :class="
              activeSection === section.id
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="abrirSecao(section.id)"
          >
            <i :class="section.icon" class="text-base shrink-0" />
            <span v-if="expandido" class="truncate">
              {{ sectionLabel(section.id) }}
            </span>
            <!-- Edição não salva na seção aberta: um ponto âmbar, o mesmo sinal do Publicar. -->
            <span
              v-if="activeSection === section.id && secaoSuja"
              data-secao-suja
              :title="t('POWER_AGENTS.UNSAVED.DOT')"
              class="absolute rounded-full top-1.5 right-1.5 size-2 bg-n-amber-9"
            />
          </button>

          <!-- O alfinete só aparece com o menu aberto: colapsado ele seria mais um ícone
               competindo com as seções, e sem rótulo ninguém adivinha o que faz. -->
          <button
            v-if="expandido"
            v-tooltip.right="
              fixado ? t('POWER_AGENTS.MENU.UNPIN') : t('POWER_AGENTS.MENU.PIN')
            "
            class="flex items-center gap-2.5 px-3 py-2 mt-auto text-xs rounded-lg text-left transition-colors text-n-slate-10 hover:bg-n-alpha-1"
            @click="alternarFixado"
          >
            <i
              class="text-base shrink-0"
              :class="fixado ? 'i-lucide-pin-off' : 'i-lucide-pin'"
            />
            <span class="truncate">
              {{
                fixado
                  ? t('POWER_AGENTS.MENU.UNPIN')
                  : t('POWER_AGENTS.MENU.PIN')
              }}
            </span>
          </button>
        </nav>
      </div>

      <section class="flex-1 min-w-0 p-4 overflow-y-auto sm:p-6">
        <component :is="activeComponent" :agent="agent" />
      </section>
    </div>

    <Dialog
      ref="publishDialogRef"
      :title="t('POWER_AGENTS.PUBLISH.CONFIRM_TITLE')"
      :description="t('POWER_AGENTS.PUBLISH.CONFIRM_BODY')"
      :confirm-button-label="
        secaoSuja
          ? t('POWER_AGENTS.PUBLISH.SAVE_AND_PUBLISH')
          : t('POWER_AGENTS.PUBLISH.CONFIRM_YES')
      "
      :cancel-button-label="t('POWER_AGENTS.PUBLISH.CONFIRM_NO')"
      :is-loading="isPublishing"
      @confirm="confirmPublish"
    >
      <p
        v-if="secaoSuja"
        data-aviso-nao-salvo
        class="flex items-start gap-2 px-3 py-2.5 m-0 text-sm rounded-lg bg-n-amber-2 text-n-amber-11"
      >
        <i class="i-lucide-circle-alert size-4 mt-0.5 shrink-0" />
        {{
          t('POWER_AGENTS.PUBLISH.UNSAVED_NOTE', {
            secao: sectionLabel(activeSection),
          })
        }}
      </p>
    </Dialog>

    <!-- Guarda da seção suja: três saídas, e a mais segura (ficar) é o Esc / fechar. -->
    <Dialog
      ref="guardaRef"
      :title="t('POWER_AGENTS.UNSAVED.TITLE')"
      :description="
        t('POWER_AGENTS.UNSAVED.BODY', { secao: sectionLabel(activeSection) })
      "
      @close="esquecerTroca"
    >
      <template #footer>
        <div class="flex flex-wrap items-center justify-end w-full gap-2">
          <Button
            :label="t('POWER_AGENTS.UNSAVED.STAY')"
            slate
            ghost
            sm
            @click="ficar"
          />
          <Button
            :label="t('POWER_AGENTS.UNSAVED.DISCARD')"
            ruby
            faded
            sm
            data-descartar
            @click="descartarETrocar"
          />
          <Button
            :label="t('POWER_AGENTS.UNSAVED.SAVE')"
            sm
            data-salvar-e-seguir
            :is-loading="salvandoNaGuarda"
            @click="salvarETrocar"
          />
        </div>
      </template>
    </Dialog>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('POWER_AGENTS.DELETE.TITLE')"
      :description="
        t('POWER_AGENTS.DELETE.CONFIRM', {
          name: agent?.bot_name || agent?.slug,
        })
      "
      :confirm-button-label="t('POWER_AGENTS.DELETE.YES')"
      :cancel-button-label="t('POWER_AGENTS.DELETE.NO')"
      :is-loading="isDeleting"
      @confirm="confirmDelete"
    />
  </div>
</template>

<!-- Sem `scoped` de propósito: o grifo cai num elemento de uma SEÇÃO FILHA, e um estilo
     escopado no pai nunca o alcançaria. O nome é prefixado (`pa-`) porque global sem prefixo
     é colisão esperando acontecer. -->
<style>
.pa-grifado {
  border-radius: 0.75rem;
  animation: pa-grifo 3s ease-out;
}

/* Anel que apaga, e não uma cor que fica: o grifo é para dizer "é aqui" e sair do caminho.
   Um destaque permanente vira parte do visual do campo e deixa de significar alguma coisa. */
@keyframes pa-grifo {
  0%,
  55% {
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.55);
    background-color: rgba(245, 158, 11, 0.09);
  }
  100% {
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0);
    background-color: transparent;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pa-grifado {
    animation: none;
    box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.55);
  }
}
</style>
